import { useMemo } from 'react';
import { useAuth } from '../state/AuthContext';
import type { SubscriptionAccess, TierId } from './types';

// Server-side feature flags, mirror of server FEATURE_REQUIREMENTS keys.
export type FeatureKey =
  // Conv-16 — AI features (keys mirror FEATURE_REQUIREMENTS in sansara-server.mjs)
  | 'ai_portfolio_qa'
  | 'ai_doc_intel'
  | 'ai_assistant'
  | 'core_dashboard'
  | 'secure_auth'
  | 'manual_tracking'
  | 'watchlists_basic'
  | 'alerts_basic'
  | 'corporate_actions'
  | 'learn_library'
  | 'watchlists_unlimited'
  | 'alerts_unlimited'
  | 'portfolio_analytics'
  | 'goals_tracking'
  | 'sip_planner'
  | 'strategy_lenses'
  | 'account_aggregator'
  | 'zerodha_connect'
  | 'trade_preflight'
  // 'household_views' removed (Conv-22): dead Platinum key, unenforced. Household is
  // Gold via 'family_aggregation'. Kept out so the client type can't reference a
  // feature the server no longer publishes.
  | 'concierge_review'
  // Batch 2 — Structure & HNI trust
  | 'family_aggregation'
  | 'estate_vault'
  | 'entity_management'
  | 'access_controls'
  | 'proof_of_wealth'
  // Batch 3 — Analytics & tracking
  | 'index_benchmark'
  | 'technical_indicators'
  | 'margin_tracking'
  | 'dividend_calendar'
  | 'scenario_projections'
  | 'private_investments'
  | 'capital_gains_ledger'
  | 'fire_planner'
  // Batch 4 — surfaces
  | 'home_widget'
  | 'voice_commands'
  | 'smartwatch'
  | 'notification_sounds'
  // Batch 5 — engagement
  | 'daily_digest'
  | 'portfolio_summary'
  | 'nominee_hub'
  | 'behavioural_nudges'
  // Batch 6 — growth & social
  | 'peer_benchmark'
  | 'masked_sharing'
  | 'api_access'
  // Batch 7 — desktop & long-tail
  | 'desktop_portal'
  | 'joint_accounts'
  | 'bond_yield_curve'
  | 'macro_indicators'
  | 'loyalty_points'
  // Batch 8 — trackers
  | 'depreciation_calculator'
  | 'charitable_giving'
  | 'carbon_footprint'
  | 'learn_earn'
  | 'commodity_tracking'
  // Batch 9 — derivatives dark shells (built, waiting on licences)
  | 'options_dashboard'
  | 'options_builder'
  | 'options_rolling'
  | 'smart_order_routing'
  | 'level2_depth'
  // Batch 10 — advice-engine dark shells (behind SEBI RA/RIA flags)
  | 'tax_loss_harvesting'
  | 'rebalance_scheduler'
  | 'algo_builder'
  | 'thematic_baskets'
  | 'analyst_reports'
  // Batch 11 — data-partner dark shells (behind partner-credential flags)
  | 'nps_integration'
  | 'epf_integration'
  | 'property_valuation'
  | 'cross_border_tax'
  | 'cash_flow'
  // Batch 12 — execution/agent dark shells (nothing executable; broker/FIU-IND)
  | 'trade_reversal'
  | 'agent_accounts'
  | 'mean_reversion'
  | 'flash_crash_listener'
  | 'hardware_wallet'
  // Aggregation — CAS-PDF importer + Phase C/D dark rails
  | 'cas_import'
  | 'broker_connections'
  | 'liabilities_tracking'
  | 'insurance_tracking'
  // Robo-advising dark shell (Platinum; behind SEBI RIA registration)
  | 'risk_profiling'
  | 'model_portfolios'
  | 'robo_rebalancing';

// Ordered lowest → highest. Mirror of server TIER_ORDER. Used for upgrade hints.
// Conv-56: the floor is FREE again — every account starts there and stays until
// they buy something.
export const TIER_ORDER: TierId[] = ['FREE', 'GOLD_ELITE', 'PLATINUM_ELITE'];
// Legacy alias: an old persisted entitlements blob may still say SILVER_ELITE;
// the contents moved with the name, so the alias is exact.
export const LEGACY_TIER_ALIAS: Partial<Record<TierId, TierId>> = { SILVER_ELITE: 'FREE' };

/**
 * Conv-37 default when the session predates `entitlements.access`.
 *
 * ACTIVE, deliberately: an older server response must never lock the app. The
 * server is the enforcement boundary (it answers 402 on every non-exempt route),
 * so failing OPEN here costs nothing and failing closed would black out a working
 * account the moment a stale payload arrived.
 */
const ASSUMED_ACTIVE: SubscriptionAccess = {
  state: 'ACTIVE',
  active: true,
  heldOpen: false,
  endsAt: null,
  daysRemaining: null,
  cancelled: false,
};

export interface UseEntitlements {
  tier: TierId;
  isFounder: boolean;
  /**
   * True when the current session can use the given feature key.
   *
   * Conv-37: this is `featureAccess[key] && access.active` — an EXPIRED account
   * still carries the tier it lapsed from, so entitlement alone would render a
   * full Platinum app for someone the server answers 402 to on every route.
   */
  can: (feature: FeatureKey) => boolean;
  /** Convenience: tier index in TIER_ORDER (0 = Free, 2 = Platinum). */
  rank: number;
  /**
   * Display name of the feature's minimum tier (e.g. "Gold Elite"), straight
   * from the server's featureTiers map — the single source for lock copy.
   * Null when the session predates the field (fallback to any local prop).
   */
  tierFor: (feature: FeatureKey) => string | null;
  /**
   * Conv-29: can a paid upgrade actually be COMPLETED right now? False in prod
   * while Razorpay is unconfigured — every checkout dead-ends in a 409 loop.
   * CTAs read this to show an honest "opens at launch" state. Defaults true for
   * a session predating the field (the pre-fix behaviour, still guarded server-
   * side by the 402/409 boundary).
   */
  upgradesAvailable: boolean;
  /**
   * Conv-37 — the trial/expiry state, straight from the server. ONE source for
   * every surface that talks about the trial, a lapse or a renewal date.
   */
  access: SubscriptionAccess;
  /** The account is inside its one-month trial. */
  isTrialing: boolean;
  /** Whole days left on the current window (trial, period or grace), or null. */
  daysRemaining: number | null;
  /**
   * The subscription lapsed but access continues because no upgrade can be
   * completed right now. A surface must SAY so, never pretend nothing happened.
   */
  heldOpen: boolean;
  /** Length of the free trial in days, as the server defines it. */
  trialDays: number;
}

/**
 * Frontend mirror of the server's feature gate. Reads from session
 * entitlements so there is a single source of truth — the server.
 *
 * Use this to decide whether to render a screen, show a lock overlay,
 * or grey out a CTA. The server is still the enforcement boundary: a
 * gated UI button is a courtesy, not a security control.
 */
export function useEntitlements(): UseEntitlements {
  const { session } = useAuth();
  // MEMOISED ON THE SESSION, and that is load-bearing rather than a micro-optimisation.
  //
  // This used to build a new object — and a new `can` closure — on EVERY render. Any
  // consumer that put `can` (or the returned object) in a hook dependency array
  // therefore had that effect re-run on every render, for ever. The first-run tour
  // did exactly that: its `begin` was `useCallback([can])`, so the autostart effect
  // re-armed on every render and threw the user back to card 1 a second after they
  // pressed Next — an infinite loop out of which the only exit was Skip. Reported
  // from a real phone, and structurally identical to the Conv-30 defect where a
  // changing `t` identity re-fired real API GETs on six pages.
  //
  // `session` is a context value that changes identity only when the session
  // actually changes, so this is stable across unrelated re-renders while still
  // updating the instant entitlements do. Keep it memoised; a future consumer will
  // put one of these values in a dep array and must not be punished for it.
  return useMemo<UseEntitlements>(() => {
    // A legacy SILVER_ELITE blob maps to the FREE floor (exact alias).
    const rawTier = session?.entitlements?.tier ?? 'FREE';
    const tier = (LEGACY_TIER_ALIAS[rawTier as TierId] ?? rawTier) as TierId;
    const featureAccess = session?.entitlements?.featureAccess ?? {};
    const featureTiers = session?.entitlements?.featureTiers ?? {};
    const upgradesAvailable = session?.entitlements?.upgradesAvailable ?? true;
    const access = session?.entitlements?.access ?? ASSUMED_ACTIVE;
    const rank = TIER_ORDER.indexOf(tier);
    return {
      tier,
      isFounder: Boolean(session?.isFounder),
      // Both halves, always: entitlement AND a live subscription.
      can: (feature) => Boolean(featureAccess[feature]) && access.active,
      rank: rank === -1 ? 0 : rank,
      tierFor: (feature) => featureTiers[feature] ?? null,
      upgradesAvailable,
      access,
      isTrialing: access.state === 'TRIALING',
      daysRemaining: access.daysRemaining ?? null,
      heldOpen: Boolean(access.heldOpen),
      trialDays: session?.entitlements?.trialDays ?? 30,
    };
  }, [session]);
}
